import { useDndContext, useDroppable, type ClientRect } from "@dnd-kit/core";
import { Link } from "@inertiajs/react";
import LotusIcon from "@/components/LotusIcon";
import { blockHeaderShift } from "@/lib/blockSorting";

export interface MyDayBlockDropData {
  kind: "myDayBlock";
  sequential: boolean;
}

interface Props {
  sequential: boolean;
  emptyHint?: string;
  zenHref?: string;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  between?: BlockBoundary;
}

export interface BlockBoundary {
  rowIds: string[];
  boundary: number;
}

// Also a drop target: a task dropped on the title lands first in the block — the only
// way into a block that has no rows yet.
export default function MyDayBlockHeader({ sequential, emptyHint, zenHref, collapsed = false, onToggleCollapsed, between }: Props) {
  const data: MyDayBlockDropData = { kind: "myDayBlock", sequential };
  const { setNodeRef, isOver } = useDroppable({
    id: sequential ? "my-day-block:in-order" : "my-day-block:any-time",
    data,
  });

  const frame = emptyHint
    ? `border border-dashed px-3 py-2 ${isOver ? "border-blue-400 bg-blue-50 dark:bg-blue-950/40" : "border-gray-300 dark:border-gray-700"}`
    : `px-1 pt-4 pb-2 ${isOver ? "bg-blue-50 dark:bg-blue-950/40" : ""}`;

  const { active, over, droppableRects } = useDndContext();
  const shift = between
    ? blockHeaderShift(between.boundary, {
        activeIndex: active ? between.rowIds.indexOf(String(active.id)) : -1,
        overIndex: over ? between.rowIds.indexOf(String(over.id)) : -1,
        rects: between.rowIds.map((id) => droppableRects.get(id) as ClientRect),
      })
    : 0;

  const title = sequential ? "По порядку" : "В любое время";

  return (
    <div
      ref={setNodeRef}
      data-testid={sequential ? "my-day-block-in-order" : "my-day-block-any-time"}
      className={`rounded-lg text-xs font-medium text-gray-400 dark:text-gray-500 ${frame}`}
      style={active ? { transform: `translateY(${shift}px)`, transition: "transform 200ms ease" } : undefined}
    >
      <div className="flex items-center gap-1">
        {onToggleCollapsed ? (
          <button
            type="button"
            aria-expanded={!collapsed}
            onClick={onToggleCollapsed}
            className="flex items-center gap-1 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg
              className={`w-3 h-3 transition-transform ${collapsed ? "" : "rotate-90"}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {title}
          </button>
        ) : (
          title
        )}
        {zenHref && (
          <Link
            href={zenHref}
            aria-label="Дзен-режим по блоку"
            title="Дзен-режим по блоку"
            className="hover:text-blue-500"
          >
            <LotusIcon className="w-4 h-4" />
          </Link>
        )}
      </div>
      {emptyHint && <div className="font-normal">{emptyHint}</div>}
    </div>
  );
}
